/**
 * Module-level state holder for the active support-mode session.
 *
 * Why a module-level singleton rather than React Context?
 *
 * The supabase client is a module-level singleton too (`./client.ts`) — it's
 * created once at boot and shared by every hook, edge function, and helper
 * in the app. Its `global.fetch` wrapper (also defined at boot) needs to
 * read support-mode state on every request to (a) inject the
 * `x-support-session-id` header so DB-side RLS predicates can scope tenant
 * access via `current_setting('request.headers')`, and (b) emit a
 * `support_session_audit` row after each mutation so the
 * `AdminSupportSessions` "Actions Performed" timeline becomes truthful.
 *
 * That fetch wrapper executes outside any React tree, so it can't subscribe
 * to a context. A plain module-level ref written by `SupportModeProvider`
 * is the simplest correct shape — same pattern Sentry and PostHog use for
 * their global breadcrumb / user state.
 *
 * This is intentionally not exported through a hook. The ONLY writers are
 * `SupportModeProvider.enterSupportMode` / `.exitSupportMode`; the ONLY
 * reader is the fetch wrapper in `./client.ts`. Anything else should read
 * the React state via `useSupportMode()` instead.
 */

export interface ActiveSupportMode {
  /** UUID of the row in `public.support_sessions`. Stamped into every audit row. */
  sessionId: string;
  /** The tenant org the admin is "supporting" — target of every audited write. */
  targetOrgId: string;
  /** auth.uid() of the platform admin operating the session. */
  adminUserId: string;
}

let active: ActiveSupportMode | null = null;

export function setActiveSupportMode(state: ActiveSupportMode | null): void {
  active = state;
}

export function getActiveSupportMode(): ActiveSupportMode | null {
  return active;
}
